import React from "react";
import { Box, CardHeader, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Chart } from "./chart";
import { ApexOptions } from "apexcharts";
import { StandardCard } from "../../../../components/Cards";
import {
  CHART_STATUS_LABELS,
  getChartColors,
  IStatusCount,
  processWhatsappCounts,
} from "../../../market-campaign/constants/statusConfig";

interface PieChartProps {
  sx?: any;
  title: string;
  subheader: string;
  isLoading: boolean;
  statusCounts: IStatusCount[];
  selectedSeries: string[];
  onSeriesChange: (series: string[]) => void;
}

export const PieChart = ({
  sx,
  title,
  subheader,
  isLoading,
  statusCounts,
  selectedSeries,
  onSeriesChange,
  ...other
}: PieChartProps) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  // Transform the status counts data into chart data
  const transformStatusData = () => {
    // Process counts to group unknown statuses under FAILED
    const processedCounts = processWhatsappCounts(statusCounts);

    // Create a map of status counts for easy lookup
    const statusCountsMap = new Map<string, number>();
    processedCounts?.forEach((item) => {
      statusCountsMap.set(item.status, item.cnt);
    });

    // Use all status labels and get counts from the map (default to 0 if not present)
    const seriesData = CHART_STATUS_LABELS.map((item, index) => {
      const count = statusCountsMap.get(item.value) || 0;
      const color = chartColors[index % chartColors.length];

      return {
        x: item.label,
        y: count,
        fill: color,
      };
    });

    return {
      series: seriesData.map((item) => item.y),
      labels: seriesData.map((item) => item.x),
      colors: seriesData.map((item) => item.fill),
    };
  };

  const chartData = transformStatusData();
  const totalMessages = chartData.series.reduce((a, b) => a + b, 0);

  const chartOptions: ApexOptions = React.useMemo(
    () => ({
      chart: {
        type: "donut",
      },
      labels: chartData.labels,
      colors: chartData.colors,
      dataLabels: {
        enabled: true,
        formatter: function (val: number) {
          return val > 0 ? `${val.toFixed(1)}%` : "";
        },
        dropShadow: {
          enabled: false,
        },
        style: {
          fontSize: "12px",
          fontWeight: 600,
        },
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "12px",
        itemMargin: {
          horizontal: 8,
          vertical: 4,
        },
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} message${val !== 1 ? "s" : ""}`,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "55%",
            labels: {
              show: true,
              total: {
                show: true,
                label: "Total Messages",
                fontSize: "12px",
                fontWeight: 600,
                formatter: function () {
                  if (totalMessages === 0) return "No messages";
                  return `${totalMessages}`;
                },
              },
            },
          },
        },
      },
      responsive: [
        {
          breakpoint: 1200,
          options: {
            plotOptions: {
              pie: {
                donut: {
                  size: "45%",
                  labels: {
                    total: {
                      fontSize: "7px",
                      style: {
                        fontSize: "7px",
                      },
                    },
                  },
                },
              },
            },
            dataLabels: {
              style: {
                fontSize: "11px",
              },
            },
            legend: {
              fontSize: "11px",
            },
          },
        },
        {
          breakpoint: 768,
          options: {
            plotOptions: {
              pie: {
                donut: {
                  size: "55%",
                  labels: {
                    total: {
                      fontSize: "8px",
                      style: {
                        fontSize: "8px",
                      },
                    },
                  },
                },
              },
            },
            dataLabels: {
              style: {
                fontSize: "10px",
              },
            },
            legend: {
              fontSize: "10px",
              itemMargin: {
                horizontal: 6,
                vertical: 3,
              },
            },
          },
        },
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 250,
            },
            plotOptions: {
              pie: {
                donut: {
                  size: "55%",
                  labels: {
                    total: {
                      fontSize: "8px",
                      style: {
                        fontSize: "5px",
                      },
                    },
                  },
                },
              },
            },
            dataLabels: {
              style: {
                fontSize: "9px",
              },
            },
            legend: {
              fontSize: "9px",
              itemMargin: {
                horizontal: 4,
                vertical: 2,
              },
            },
          },
        },
      ],
    }),
    [chartData, totalMessages]
  );

  return (
    <StandardCard sx={sx} {...other}>
      <CardHeader title={title} subheader={subheader} />

      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          pb: { xs: 1, sm: 1 },
          height: { xs: 280, sm: 320, md: 350 },
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Chart
            dir="ltr"
            type="donut"
            series={chartData.series}
            options={chartOptions}
            width="100%"
            height="100%"
          />
        )}
      </Box>
    </StandardCard>
  );
};
